import React, { useCallback, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import ModalRight from '../../../../../../SharedComponents/ModalRight/ModalRight';
import CloseIcon from '../../../../../../SharedComponents/svg/Icons/CloseIcon';
import styles from './TiktokAudioModal.module.css';
import MetricCardWithChart from '../../../../../../ui/DataDisplay/Statistics/MetricCardWithChart/MetricCardWithChart';
import MusicNoteIcon from '../../../../../../SharedComponents/svg/Icons/MusicNoteIcon';
import CalendarIcon from '../../../../../../SharedComponents/svg/Icons/CalendarIcon';
import {
    buildTiktokMusicUrl,
    formatNumberToKNotation,
    numberWithCommas,
    roundTo2Dp,
    showNotification,
} from '../../../../../../helpers';
import ViewIcon from '../../../../../../SharedComponents/svg/Icons/ViewIcon';
import { ExploreTiktokAudiosTableRowV2, TiktokAudiosTableRowV2 } from '../../../../TikTok.types';
import AudioPlaybackThumbnail from '../../../../../../ui/AudioPlaybackThumbnail/AudioPlaybackThumbnail';
import RefreshAudioDataButton from './Monitoring/RefreshAudioDataButton';
import { ProtectedByUserGroups } from '../../../../../Auth/components/ProtectedByUserGroup/ProtectedByUserGroups';
import Skeleton from '../../../../../../ui/Skeleton/Skeleton';
import CustomNameInput from '../../../components/CustomNameInput/CustomNameInput';
import {
    ClientMonitoredTiktokAudioRelationship,
    patchClientMonitoredTiktokAudioRelationship,
    triggerTiktokAudioScraping,
} from '@round/api';
import AddCustomName from './AddCustomName/AddCustomName';
import useTiktokAudioCreationsStats from './useTiktokAudioCreationsStats';
import useTopPosts from './useTopPosts';
import useMonitoredAudio from './useMonitoredAudio';
import TopPosts from './TopPosts/TopPosts';
import VideoCreationsCharts from './VideoCreationsCharts/VideoCreationsCharts';
import useTiktokAudio from './useTiktokAudio';
import AudioTrackingType from './Monitoring/AudioTrackingType';
import ReliabilityStatus from './Monitoring/ReliabilityStatus';
import useAbortableEffect from '../../../../../../Hooks/useAbortableEffect';
import { useAudioViewStats } from './useAudioViewStats';
import countries from '../../../../../../utility/countries';
import { ProgressBar } from '@round/ui-kit';

type TiktokAudioModalProps = {
    isModalOpen: boolean;
    closeModal: () => void;
    audioId: number | undefined;
    monitoredAudioStats?: ExploreTiktokAudiosTableRowV2 | TiktokAudiosTableRowV2 | null;
    onAlternateNameUpdated?: (relation: ClientMonitoredTiktokAudioRelationship) => void;
    onAlternateNameAdded?: (relation: ClientMonitoredTiktokAudioRelationship) => void;
    modalClassName?: string;
    renderCloseButton?: (close: () => void) => React.ReactNode;
};

const TiktokAudioModal = ({
    isModalOpen,
    closeModal,
    audioId,
    monitoredAudioStats,
    onAlternateNameUpdated,
    modalClassName,
    renderCloseButton = (close) => (
        <button className={styles.closeButton} title="close modal" onClick={close}>
            <CloseIcon width={16} height={16} />
        </button>
    ),
}: TiktokAudioModalProps) => {
    const refreshAbortController = useRef<AbortController | null>(null);

    const {
        tiktokAudio,
        error: tiktokAudioError,
        isLoading: tiktokAudioLoading,
        reset: resetTiktokAudio,
        abortController: tiktokAudioAbortController,
    } = useTiktokAudio(audioId);

    const monitoredAudioIds: number[] = useMemo(() => {
        if (!monitoredAudioStats) {
            return tiktokAudio?.monitored_audio_id ?? [];
        }

        return typeof monitoredAudioStats.monitored_music_id === 'number'
            ? [monitoredAudioStats.monitored_music_id]
            : [];
    }, [monitoredAudioStats, tiktokAudio?.monitored_audio_id]);

    const {
        monitoredAudio,
        abortController: monitoredAudioAbortController,
        reset: resetMonitoredAudio,
        isUpdateTrackingTypeLoading,
        updateMonitoredAudioTrackingType,
    } = useMonitoredAudio(monitoredAudioIds[0]);

    const {
        creationsStats,
        isLoading: creationsStatsLoading,
        errorLoading: errorLoadingCreationsStats,
        refresh: refreshCreationsStats,
        abortController: creationsStatsAbortController,
        reset: resetCreationsStats,
    } = useTiktokAudioCreationsStats(audioId);

    const {
        topPosts,
        count: topPostsCount,
        abortController: topPostsAbortController,
        refresh: refreshTopPosts,
        reset: resetTopPosts,
        hasErrorLoading: errorLoadingTopPosts,
        isLoading: topPostsLoading,
        parameterHandlers: topPostsParameterHandlers,
    } = useTopPosts(audioId);

    const {
        viewStats,
        isLoading: isViewStatsLoading,
        isInitialized: isViewStatsInitialized,
        init: initViewStats,
        refresh: refreshViewStats,
        reset: resetViewStats,
    } = useAudioViewStats(audioId);

    const viewStatsAbortController = useAbortableEffect(
        (signal) => {
            if (audioId && !isViewStatsInitialized) {
                initViewStats({ signal });
            }
        },
        [initViewStats, isViewStatsInitialized, audioId]
    );

    const audioInfoLoading = tiktokAudioLoading && !monitoredAudioStats;
    const audioImage = monitoredAudioStats?.image_url ?? tiktokAudio?.cover_thumb;
    const audioPlayUrl = monitoredAudioStats?.audioUrl ?? tiktokAudio?.play_url;
    const audioTiktokId = monitoredAudioStats?.tiktok_id ?? tiktokAudio?.tiktok_id;
    const audioTitle = monitoredAudioStats?.title ?? tiktokAudio?.title;
    const isOriginalAudio = monitoredAudioStats?.is_original ?? tiktokAudio?.is_original;

    const clientMonitoredTiktokAudioRelations = useMemo(
        () => monitoredAudioStats?.clientMonitoredAudioRelation ?? [],
        [monitoredAudioStats?.clientMonitoredAudioRelation]
    );

    const totalViews = viewStats?.total_views || null;
    const totalViewsPostCount = viewStats?.total_views_post_count || null;
    const totalViewsPostCountLabel = totalViewsPostCount ? `Based on ${totalViewsPostCount} posts` : undefined;

    const topLocations = !!viewStats?.locations.countries.length ? viewStats?.locations.countries : null;
    const topLocationsPostCount = viewStats?.location_post_count || 100;
    const topLocationsPostCountLabel = topLocationsPostCount ? `Based on ${topLocationsPostCount} posts` : undefined;

    const [refreshLoading, setRefreshLoading] = useState(false);
    const statsLastUpdatedDate = useMemo(() => {
        const dates = creationsStats?.map((stats) => Date.parse(stats.timestamp)) ?? [];
        return new Date(Math.max(...dates));
    }, [creationsStats]);

    const totalCreations = useMemo(() => {
        if (monitoredAudioStats) {
            return monitoredAudioStats.video_count;
        }

        return creationsStats[creationsStats.length - 1]?.video_count ?? null;
    }, [monitoredAudioStats, creationsStats]);

    const dailyCreations = useMemo(() => {
        if (monitoredAudioStats) {
            return monitoredAudioStats.video_count_daily_change;
        }

        return creationsStats[creationsStats.length - 1]?.change ?? null;
    }, [creationsStats, monitoredAudioStats]);

    const refreshStats = useCallback(async () => {
        if (!audioId || !monitoredAudioIds.length) {
            return;
        }

        try {
            refreshAbortController.current = new AbortController();
            const signal = refreshAbortController.current.signal;
            setRefreshLoading(true);
            const triggerResponses = await Promise.all(
                monitoredAudioIds.map((monitoredAudioId) => triggerTiktokAudioScraping(monitoredAudioId))
            );

            if (triggerResponses.some((response) => response.status !== 204)) {
                showNotification('Could not trigger scraping for all monitored audios', 'warning');
            }

            await Promise.all([
                refreshCreationsStats({ signal }),
                refreshTopPosts({ signal }),
                refreshViewStats({ signal }),
            ]);
            showNotification('Refresh successful', 'info');
        } catch (e) {
            if (e instanceof Error && e.name === 'AbortError') {
                return;
            }

            showNotification('Refresh unsuccessful', 'error');
        } finally {
            setRefreshLoading(false);
        }
    }, [audioId, refreshTopPosts, monitoredAudioIds, refreshCreationsStats, refreshViewStats]);

    const handleClose = useCallback(() => {
        resetTiktokAudio();
        resetCreationsStats();
        resetTopPosts();
        resetMonitoredAudio();
        resetViewStats();

        tiktokAudioAbortController.abort();
        creationsStatsAbortController.abort();
        topPostsAbortController.abort();
        viewStatsAbortController.abort();
        monitoredAudioAbortController.abort();
        if (refreshAbortController.current) {
            refreshAbortController.current.abort();
            setRefreshLoading(false);
        }
        closeModal();
    }, [
        closeModal,
        creationsStatsAbortController,
        monitoredAudioAbortController,
        resetCreationsStats,
        resetMonitoredAudio,
        resetTiktokAudio,
        resetTopPosts,
        resetViewStats,
        tiktokAudioAbortController,
        topPostsAbortController,
        viewStatsAbortController,
    ]);

    const updateCustomName = useCallback(
        async (customName: ClientMonitoredTiktokAudioRelationship) => {
            const relation = clientMonitoredTiktokAudioRelations.find((rel) => rel.client_id === customName.client_id);
            if (!relation) {
                return;
            }

            try {
                const response = await patchClientMonitoredTiktokAudioRelationship(relation.id, {
                    custom_name: customName.custom_name,
                });

                if (response.status !== 200) {
                    throw new Error('Could not update custom name');
                }

                showNotification('Updated', 'info');
                if (typeof onAlternateNameUpdated === 'function') {
                    onAlternateNameUpdated(response.data);
                }
            } catch {
                showNotification('Could not update custom name', 'error');
            }
        },
        [clientMonitoredTiktokAudioRelations, onAlternateNameUpdated]
    );

    const relationsWithCustomName = clientMonitoredTiktokAudioRelations.filter(
        (relation) => relation.custom_name.length > 0
    );

    const relationsWithoutCustomName = clientMonitoredTiktokAudioRelations.filter(
        (relation) => relation.custom_name.length === 0
    );

    return (
        <ModalRight isModalOpen={isModalOpen} closeModal={handleClose} className={cn(styles.modal, modalClassName)}>
            <div className={styles.container}>
                {renderCloseButton(handleClose)}
                <header className={styles.header}>
                    <AudioPlaybackThumbnail
                        loading={audioInfoLoading}
                        className={styles.audioCover}
                        audioUrl={audioPlayUrl}
                        imageUrl={audioImage}
                    />

                    <div className={styles.audioDetails}>
                        {tiktokAudioError && (
                            <span>{tiktokAudioError || "Sorry, we're experiencing technical issues"}</span>
                        )}
                        <div className={styles.titleContainer}>
                            {audioInfoLoading ? (
                                <Skeleton className={styles.title} />
                            ) : (
                                <a
                                    className={styles.title}
                                    href={buildTiktokMusicUrl(audioTitle ?? 'original-sound', audioTiktokId ?? '')}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {audioTitle}
                                </a>
                            )}

                            <div className={styles.customNames}>
                                {relationsWithCustomName.map((rel) => (
                                    <CustomNameInput
                                        key={rel.client_id}
                                        clientMonitoredAudioRelationship={rel}
                                        onSubmit={updateCustomName}
                                    />
                                ))}
                                {relationsWithoutCustomName.length > 0 && (
                                    <AddCustomName
                                        clientMonitoredTiktokAudioRelations={clientMonitoredTiktokAudioRelations}
                                        onCustomNameAdded={(relation) => {
                                            if (typeof onAlternateNameUpdated === 'function') {
                                                onAlternateNameUpdated(relation);
                                            }
                                        }}
                                    />
                                )}
                            </div>
                        </div>
                        {typeof isOriginalAudio === 'boolean' && (
                            <span
                                className={cn(styles.badge, {
                                    [styles.original]: isOriginalAudio,
                                    [styles.official]: !isOriginalAudio,
                                })}
                            >
                                {isOriginalAudio ? 'Original' : 'Official'}
                            </span>
                        )}
                    </div>

                    <ProtectedByUserGroups groups={['user_admin', 'trnds_admin']} hasAccessWhenMatches="any">
                        {monitoredAudio && (
                            <>
                                <ReliabilityStatus monitoredAudio={monitoredAudio} />

                                <AudioTrackingType
                                    monitoredAudio={monitoredAudio}
                                    loading={isUpdateTrackingTypeLoading}
                                    onChange={updateMonitoredAudioTrackingType}
                                />
                            </>
                        )}

                        {monitoredAudioIds.length > 0 && audioId && (
                            <div className={styles.refreshContainer}>
                                <RefreshAudioDataButton
                                    loading={refreshLoading}
                                    lastUpdated={statsLastUpdatedDate}
                                    onClick={refreshStats}
                                />
                            </div>
                        )}
                    </ProtectedByUserGroups>
                </header>

                <div data-test-id="headline-stats" className={styles.statsContainer}>
                    <MetricCardWithChart
                        loading={audioInfoLoading}
                        baseColor="#4CC8D9"
                        value={totalCreations}
                        label="Total creations"
                        Icon={MusicNoteIcon}
                    />

                    <MetricCardWithChart
                        loading={audioInfoLoading}
                        baseColor="#0090FF"
                        value={dailyCreations}
                        label="Daily creations"
                        Icon={CalendarIcon}
                        format={(value) => `${value > 0 ? '+' : ''}${formatNumberToKNotation(value)}`}
                    />

                    <MetricCardWithChart
                        loading={isViewStatsLoading}
                        baseColor="#48D98A"
                        value={totalViews}
                        subtitle={totalViewsPostCountLabel}
                        label="Total views (est.)"
                        Icon={ViewIcon}
                    />
                </div>

                <section>
                    <VideoCreationsCharts
                        key={audioId}
                        errorLoading={errorLoadingCreationsStats}
                        loading={creationsStatsLoading}
                        creationsStats={creationsStats}
                    />
                </section>

                {topLocations && (
                    <section>
                        <div className={styles.sectionHeader}>
                            <p className={styles.sectionTitle}>Top Locations</p>
                            <p className={styles.sectionSubtitle}>{topLocationsPostCountLabel}</p>
                        </div>
                        <div className={styles.topLocationsContainer}>
                            {topLocations
                                .sort((a, b) => b.percentage - a.percentage)
                                .map((country) => (
                                    <div key={country.code} className={styles.topLocation}>
                                        <div className={styles.locationLabel}>
                                            <span
                                                className={cn(`fi fi-${country.code.toLowerCase()}`, styles.flagIcon)}
                                            />
                                            <span className={styles.countryName}>{countries[country.code]}</span>
                                            <span>{numberWithCommas(roundTo2Dp(country.percentage * 100))}%</span>
                                        </div>

                                        <ProgressBar
                                            barStyles={{ backgroundColor: 'var(--color-primary)' }}
                                            progress={roundTo2Dp(country.percentage * 100)}
                                        />
                                    </div>
                                ))}
                        </div>
                    </section>
                )}

                <section>
                    <TopPosts
                        key={audioId}
                        topPosts={topPosts}
                        count={topPostsCount}
                        loading={topPostsLoading}
                        errorLoading={errorLoadingTopPosts}
                        {...topPostsParameterHandlers}
                    />
                </section>
            </div>
        </ModalRight>
    );
};

export default TiktokAudioModal;
